<script setup lang="ts">
import type { CourseId } from '~/data/courses'
import type { Program } from '~/data/programs'

// get params
const route = useRoute()
const params = route.params
const programId = getProgrammeIdFromQuery(route.query)

const {
  getCourseById,
  getTestimoniesByIds,
  getCourseTypeById,
  getEnrolmentsByCourseId,
  getProgramById
} = useStaticData()
const { scrollToSection } = useScroll()

const course = getCourseById(params.id as CourseId)

if (!course) {
  throw createError({
    statusCode: 404,
    message: 'Course not found',
    fatal: true
  })
}

// get enrolments
let enrolments = getEnrolmentsByCourseId(params.id as CourseId)

if (programId) {
  // filter enrolments by programme
  enrolments = enrolments.filter((e) => e.programId === programId)
}

// only programmes vs self-enrolment has comparisons
const hasComparisons = enrolments.findIndex((e) => e.comparisons) > -1

// get enrolment-related vars

// if query results in a programme
const programmeQuery = getProgramById(programId)
// find enrolments which has fees = self-enrolment type
// TODO this assumed only one self-enrolment type, which is not true, consider there will be both kydp a and kydp b ...
const selfEnrolment = programmeQuery ? null : enrolments.find((e) => e.fees)

// Filter all enrolments which has a programme Id, and it's open = programme-enrolment type
const programmeEnrolments = enrolments.filter((e) => e.programId && e.open)

// config
const runtimeConfig = useRuntimeConfig()

const siteUrl = runtimeConfig.public.siteUrl
const pageUrl = `${siteUrl}${route.path}`
const ogTitle = `${course.title} | ${runtimeConfig.public.siteName}`
const ogDescription = course.description

useSeoMeta({
  title: () => ogTitle,
  description: () => ogDescription,
  ogSiteName: () => ogTitle,
  ogTitle: () => ogTitle,
  ogDescription: () => ogDescription,
  ogUrl: () => pageUrl,
  // ogImageUrl: () => ogImageUrl,
  ogImageHeight: 630,
  ogImageWidth: 1200,
  ogImageAlt: () => ogTitle,
  ogImageType: 'image/png',
  // ogImageSecureUrl: () => ogImageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: ogTitle,
  twitterDescription: () => ogDescription
  // twitterImage: () => ogImageUrl
})

const content = computed(() => (course.hidden ? 'noindex,nofollow' : 'index,follow'))

useHeadSafe({
  meta: [{ id: 'robots', name: 'robots', content: content.value }]
})

definePageMeta({
  layout: 'student'
})

// get testimonies
const testimonials = getTestimoniesByIds(course.graduates)

// REFS
const active = ref(0)

// section refs
const overview = ref()
const modules = ref()
const enrol = ref()
const careers = ref()

// enrolment sub-section refs
const selfEnrol = ref()
const programmeEnrol = ref()

// sections
const sections = [
  {
    title: 'Overview',
    icon: defineAsyncComponent(() => import('~/components/common/icon/courses/IconOverview.vue'))
  },
  {
    title: 'Modules',
    icon: defineAsyncComponent(() => import('~/components/common/icon/courses/IconModules.vue'))
  },
  {
    title: 'Enrol',
    icon: defineAsyncComponent(() => import('~/components/common/icon/courses/IconEnrol.vue'))
  },
  {
    title: 'Careers',
    icon: defineAsyncComponent(() => import('~/components/common/icon/courses/IconOutcomes.vue'))
  }
]

const getOffset = (el: HTMLElement) => {
  return el.offsetTop - 150
}

const handleScroll = () => {
  const y = window.scrollY

  if (y > getOffset(careers.value)) active.value = 3
  else if (y > getOffset(enrol.value)) active.value = 2
  //   else if (y > getOffset(financing.value)) active.value = 2
  else if (y > getOffset(modules.value)) active.value = 1
  else active.value = 0
}

function handleScrollTo(index: number) {
  //   const el = [overview.value, modules.value, financing.value, enrol.value, careers.value][index]
  const el = [overview.value, modules.value, enrol.value, careers.value][index]
  scrollToSection(el)
}

// scrolling in enrolment sub-sections
const scrollToEnrolSection = (index: number) => {
  const el = [selfEnrol.value, programmeEnrol.value][index]
  scrollToSection(el)
}

// go to enrolment section
const goToEnrolment = () => handleScrollTo(2)

const getBtnName = () => {
  if (enrolments.length === 0) return 'Application closed'
  else if (enrolments.length === 1 && enrolments[0].direct) return 'Start learning'
  else return 'Apply now'
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const { status } = useAuth()
const { sendContactUsMessageForCourse } = useContactUs()
const notifyEmail = ref('')
async function handleNotify() {
  if (!notifyEmail.value || !course) return
  await sendContactUsMessageForCourse({
    email: notifyEmail.value,
    course_name: course.title
  })
  notifyEmail.value = ''
  alert('We will notify you when the course is open!')
}
</script>

<template>
  <div v-if="course" id="course-item" :class="status === 'authenticated' ? 'course-item-auth' : ''">
    <PageCoursesCourseHeader
      :course="course"
      :enrolment-open="enrolments.length > 0"
      :button-title="getBtnName()"
      @click-apply="goToEnrolment"
    />

    <div id="course-item-content" class="container">
      <PageCoursesCourseBar
        :sections="sections"
        :active="active"
        :enrolment-open="enrolments.length > 0"
        :button-title="getBtnName()"
        @scroll-to="handleScrollTo"
        @click-apply="goToEnrolment"
      />

      <div id="course-item-sections">
        <div ref="overview" class="course-section">
          <h6>Overview</h6>

          <h1>{{ course.overview.title }}</h1>

          <p v-html="course.overview.paragraph"/>

          <h2>What you will learn</h2>
          <PageCoursesCourseCheckpoint
            v-for="outcome in course.outcomes"
            :key="outcome"
            :title="outcome"
          />

          <h2>Languages and tools covered</h2>
          <div id="course-section-tools">
            <PageCoursesCourseTool v-for="id in course.tools" :key="id" :course-tool-id="id" />
          </div>
        </div>

        <div id="course-section-modules" ref="modules" class="course-section">
          <h6>Modules</h6>
          <h3>Explore our world-class curriculum</h3>

          <PageCoursesCourseModules :modules="course.modules" />

          <h2>Meet our course creators</h2>

          <div id="course-section-instructors">
            <PageCoursesCourseInstructor
              v-for="instructor in course.instructors"
              :id="instructor"
              :key="instructor"
            />
          </div>
        </div>

        <div id="course-section-enrolment" ref="enrol" class="course-section">
          <h6>Enrol</h6>

          <div v-if="hasComparisons && selfEnrolment?.comparisons">
            <span id="course-section-enrolment-label"
              >Choose how you will apply for this course:</span
            >

            <PageCoursesCourseEnrolTable
              :comparisons="selfEnrolment?.comparisons"
              @scroll-to-self-enrol="scrollToEnrolSection(0)"
              @scroll-to-programme-enrol="scrollToEnrolSection(1)"
            />
          </div>

          <div v-if="selfEnrolment" ref="selfEnrol">
            <div v-if="hasComparisons">
              <h2>
                <u>{{ selfEnrolment?.comparisons?.selfTitle }}</u>
              </h2>
              <p class="course-enrolment-description">
                Start your application through <b>self-paced study</b>, and learn at your own pace.
                <br >
                You can either pay the fees via a discounted one-time payment, or pay monthly.
              </p>
            </div>
            <h3 v-else>Learn at your own pace</h3>

            <PageCoursesCourseSelfEnrol
              :course="course"
              :enrolment="selfEnrolment"
              :show-access="hasComparisons ? false : true"
            />
          </div>

          <div v-if="programmeEnrolments.length > 0" ref="programmeEnrol">
            <h2 v-if="hasComparisons">
              <u>{{ selfEnrolment?.comparisons?.programmeTitle }}</u>
            </h2>
            <h3 v-else>
              Apply through our {{ programmeEnrolments.length > 1 ? 'programmes' : 'programme' }}
            </h3>

            <p v-if="programmeEnrolments.length > 1" class="course-enrolment-description">
              Start your application through our <b>sponsored programmes</b>. The programmes below
              are currently accepting applications:
            </p>

            <p v-else>
              Study this course by joining our exclusive
              <b> sponsored {{ programmeEnrolments.length > 1 ? 'programmes' : 'programme' }} </b>,
              where we train you with in-demand skills and get you a career placement with our top
              hiring partners!
            </p>

            <div id="course-section-enrolment-programme-list">
              <div v-for="enrolment in programmeEnrolments" :key="enrolment.id">
                <PageCoursesCourseProgrammeCard
                  v-if="enrolment && enrolment.programId"
                  :program="getProgramById(enrolment.programId) as Program"
                  :course="course"
                  :enrolment="enrolment"
                />
              </div>
            </div>
          </div>

          <div v-if="enrolments.length === 0" id="course-section-enrolment-closed">
            <h3>Application is currently closed</h3>
            We're sorry, but we are not taking any applicants for this course at this moment.
            <br >
            Send us your email address and we will notify you when there is an opening!

            <div id="course-section-enrolment-email">
              <input v-model="notifyEmail" placeholder="Your@email.com" >
              <CommonButton @click="handleNotify">Notify me!</CommonButton>
            </div>
          </div>
        </div>

        <div id="course-section-careers" ref="careers" class="course-section">
          <h6>Careers</h6>

          <h3>We will help you boost your career</h3>

          <p>
            Navigate tech career opportunities with the help of our Career Services team. Get
            valuable insights on interview skills, CV preparation, and building a professional
            portfolio - we will be the bridge between you and your future employers.
            <br >
            <br >
            We have built relationships with hiring managers at top companies across the region,
            creating a robust employer pipeline for TalentLabs grads. Our team is constantly
            advocating for our graduates and helping you to secure your dream job.
          </p>

          <h2>Hear from our graduates</h2>

          <div id="course-section-testimonials">
            <PageHomeTestimonialCard
              v-for="testimony in testimonials"
              :key="testimony.name"
              :testimonial="testimony"
            />
          </div>

          <h2>Still have questions?</h2>

          <CommonButton :href="`/courses/faqs?type=${getCourseTypeById(course.id)}`"
            >Go to FAQ</CommonButton
          >
        </div>
      </div>
    </div>

    <LayoutNavbarMobileBackButton />
  </div>
</template>

<style scoped lang="scss">
#course-item {
  padding-bottom: 90px;

  &-content {
    display: flex;
    align-items: flex-start;
    column-gap: 110px;
  }

  &-sections {
    flex: 1;
  }
}

#course-section {
  &-modules {
    h2 {
      margin-top: 80px;
    }
  }

  &-tools {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
  }

  &-instructors {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    padding-top: 5px;
  }

  &-approach {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 60px;
    padding-top: 5px;
  }

  &-financing {
    h3 {
      margin-bottom: 25px;
    }

    p {
      margin-top: 15px;
    }
  }

  &-enrolment {
    &-label {
      font-weight: 500;
      font-size: 1rem;
      color: var(--textLight);
    }

    &-programme-list {
      display: grid;
      gap: 20px;
      margin-top: 35px;
    }

    .course-enrolment-description {
      width: 90%;
      margin-bottom: 40px;
    }

    &-closed {
      img {
        display: block;
        margin: 50px 0 40px;
        height: 150px;
      }
    }

    &-email {
      display: flex;
      gap: 20px;
      width: 500px;
      margin-top: 30px;

      :deep(.btn-m) {
        padding: 13px 35px;
        font-size: 0.95rem;
      }
    }

    h2 {
      margin-top: 100px;
    }

    h3 {
      margin-bottom: 20px;
    }
  }

  &-careers {
    p {
      margin-top: 15px;
    }
  }

  &-testimonials {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
}

.course-section {
  border-top: 1px solid var(--borderLight);
  padding-top: 20px;
  margin-bottom: 100px;
  font-size: 1.1rem;
  line-height: 1.7rem;

  h6 {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--purple);
    margin-bottom: 20px;
  }

  h1 {
    font-size: 2.4rem;
    line-height: 2.9rem;
    margin-bottom: 27px;
  }

  h2 {
    margin: 75px 0 25px;
  }

  h2,
  h3 {
    font-size: 1.7rem;
    line-height: 2.1rem;
  }
}

.course-section:last-of-type {
  margin-bottom: 0;
}

.course-item-auth {
  margin-top: -20px;
}

@media (max-width: 1200px) {
  #course-item {
    &-content {
      gap: 90px;
    }
  }
}

@media (max-width: 1100px) {
  #course-item {
    &-content {
      gap: 90px;
    }
  }

  #course-section-instructors {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 900px) {
  #course-item-content {
    display: block;
  }

  #course-section-instructors {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

@media (max-width: 700px) {
  #course-item {
    padding-top: 10px;
  }

  #course-section {
    &-tools {
      column-gap: 7px;
      row-gap: 10px;
    }

    &-approach {
      grid-template-columns: 1fr;
      gap: 30px;
    }

    &-instructors {
      gap: 10px;
      grid-template-columns: 1fr;
    }

    &-testimonials {
      grid-template-columns: 1fr;
      gap: 15px;
    }

    &-enrolment {
      &-email {
        flex-direction: column;
        align-items: center;
        width: 100%;

        input {
          width: 100%;
        }
      }
    }
  }

  .course-section {
    padding-top: 30px;
    margin-bottom: 70px;

    h6 {
      margin-bottom: 10px;
    }

    h1 {
      font-size: 1.5rem;
      line-height: 2rem;
    }

    h2 {
      margin: 50px 0 25px;
    }

    h2,
    h3 {
      font-size: 1.3rem;
      line-height: 1.8rem;
    }
  }
}
</style>
